import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { WeatherData } from '../types/weather';
import { weatherApiService, WeatherApiStatus } from '../services/weatherApi';
import { firebaseService } from '../services/firebase';

interface WeatherApiContextType {
  status: WeatherApiStatus;
  weatherData: WeatherData | null;
  startPolling: () => void;
  stopPolling: () => void;
  fetchNow: () => Promise<void>;
  isPolling: boolean;
}

const WeatherApiContext = createContext<WeatherApiContextType | undefined>(undefined);

export const WeatherApiProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<WeatherApiStatus>({
    connected: false,
    error: null,
    lastFetch: null,
  });
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);

  // Subscribe to status changes
  useEffect(() => {
    const cleanup = weatherApiService.onStatusChange((nextStatus) => setStatus(nextStatus));
    return cleanup;
  }, []);

  // Subscribe to weather data updates
  useEffect(() => {
    const handler = (data: WeatherData) => {
      // Normalize timestamp
      const timestamp =
        typeof data.timestamp === 'number' || typeof data.timestamp === 'string'
          ? data.timestamp
          : Date.now();

      const normalized: WeatherData = { ...data, timestamp };
      setWeatherData(normalized);

      // Persist to Firebase for historical data
      firebaseService.saveReading(normalized);
    };
    const cleanup = weatherApiService.subscribe(handler);
    return cleanup;
  }, []);

  // Auto-start polling on mount
  useEffect(() => {
    weatherApiService.startPolling();
    return () => {
      weatherApiService.stopPolling();
    };
  }, []);

  const startPolling = useCallback(() => {
    weatherApiService.startPolling();
  }, []);

  const stopPolling = useCallback(() => {
    weatherApiService.stopPolling();
  }, []);

  const fetchNow = useCallback(async () => {
    try {
      await weatherApiService.fetchWeather();
    } catch (error) {
      console.error('Failed to fetch weather:', error);
    }
  }, []);

  const isPolling = weatherApiService.isPollingActive();

  return (
    <WeatherApiContext.Provider
      value={{
        status,
        weatherData,
        startPolling,
        stopPolling,
        fetchNow,
        isPolling,
      }}
    >
      {children}
    </WeatherApiContext.Provider>
  );
};

export const useWeatherApi = () => {
  const context = useContext(WeatherApiContext);
  if (!context) {
    throw new Error('useWeatherApi must be used within WeatherApiProvider');
  }
  return context;
};

